/**
 * businessSignupFields.js
 * ───────────────────────
 * Canonical field registry for the unified <BusinessSignup> form.
 *
 * Every possible input across every module is declared here exactly once.
 * Module manifests reference fields by id; they never re-declare label,
 * validator, or test id.
 *
 * Adding a new field?
 *   1. Drop it in this file with { label, type, validate, helper }
 *   2. Reference its id from any module manifest in `businessSignupManifests.js`
 *
 * Validators take the value and return either `null` (valid) or an
 * error string. Use `required(message)` from the helpers below.
 */

const required = (msg = "Required") => (v) =>
  v === undefined || v === null || String(v).trim() === "" ? msg : null;

const auMobile = (v) => {
  if (!v) return "Required";
  const d = String(v).replace(/\D/g, "");
  return /^04\d{8}$/.test(d) ? null : "Must be an AU mobile (04xxxxxxxx)";
};

const internationalPhone = (v) => {
  if (!v) return "Required";
  const d = String(v).replace(/[^\d+]/g, "");
  return d.length >= 7 ? null : "Enter a valid phone number";
};

const email = (v) => {
  if (!v) return "Required";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Invalid email";
};

const auPostcode = (v) => {
  if (!v) return "Required";
  return /^\d{4}$/.test(String(v).trim()) ? null : "Must be 4 digits";
};

const freeText = required;

// Selectable lists — kept small and country-aware.
export const AU_STATES = ["NSW","VIC","QLD","WA","SA","TAS","ACT","NT"];
export const NZ_REGIONS = ["Auckland","Wellington","Canterbury","Waikato","Otago","Bay of Plenty","Manawatu-Whanganui","Hawke's Bay","Taranaki","Northland","Southland","Tasman","Nelson","Marlborough","West Coast","Gisborne"];
export const COUNTRIES = [
  { code: "au", label: "Australia" },
  { code: "nz", label: "New Zealand" },
  { code: "us", label: "United States" },
  { code: "ca", label: "Canada" },
  { code: "uk", label: "United Kingdom" },
  { code: "row", label: "Other / international" },
];

// Vendor categories — used by Plaza, Deals, Music (venue mode).
export const VENDOR_CATEGORIES = [
  "Restaurant", "Cafe", "Bakery", "Bar / Pub", "Caterer", "Food truck",
  "Grocery / Deli", "Patisserie", "Wine / Spirits", "Brewery",
  "Salon / Beauty", "Fitness studio", "Wellness clinic",
  "Retail boutique", "Service business", "Other",
];

// Property — agency vs private.
export const PROPERTY_LISTING_TYPES = [
  { value: "agency",  label: "Real-estate agency" },
  { value: "private", label: "Private seller / landlord" },
];

// ──────────────────────────────────────────────────────────────────────
// Canonical field definitions.
// ──────────────────────────────────────────────────────────────────────
export const FIELDS = {
  // Identity
  business_name:  { label: "Business name",        type: "text",   validate: required("Business name is required"), placeholder: "e.g. Your Business Pty Ltd" },
  trading_name:   { label: "Trading name (if different)", type: "text", validate: () => null, placeholder: "Optional — the name customers actually see" },
  full_name:      { label: "Full name",            type: "text",   validate: required("Your full name is required") },
  contact_first:  { label: "First name",           type: "text",   validate: required() },
  contact_last:   { label: "Last name",            type: "text",   validate: required() },

  // Business identifiers (per country handled by <BusinessLookup>)
  abn:            { label: "ABN",                  type: "business_id", validate: required("ABN is required") },
  acn:            { label: "ACN (if Pty Ltd)",     type: "text",   validate: () => null, placeholder: "Optional" },

  // Contact
  primary_email:  { label: "Email",                type: "email",  validate: email },
  primary_phone:  { label: "Business phone",       type: "tel",    validate: internationalPhone },
  mobile:         { label: "Mobile",               type: "tel",    validate: auMobile },

  // Address (AU)
  address_line_1: { label: "Street address",       type: "address_au", validate: required("Please pick your street address") },
  address_line_2: { label: "Unit / suite",         type: "text",   validate: () => null, placeholder: "Optional" },
  suburb:         { label: "Suburb",               type: "text",   validate: required() },
  state:          { label: "State",                type: "select", validate: required(), options: AU_STATES.map((s) => ({ value: s, label: s })) },
  postcode:       { label: "Postcode",             type: "text",   validate: auPostcode, maxLength: 4 },

  // Address (international)
  country:        { label: "Country",              type: "select", validate: required(), options: COUNTRIES.map((c) => ({ value: c.code, label: c.label })) },
  city:           { label: "City",                 type: "text",   validate: required() },
  state_or_province: { label: "State / Province",  type: "text",   validate: required() },
  postal_code:    { label: "Postal / ZIP code",    type: "text",   validate: required() },

  // Module-specific (Plaza, Deals)
  vendor_category: { label: "What do you sell?",  type: "select", validate: required(), options: VENDOR_CATEGORIES.map((v) => ({ value: v, label: v })) },
  cuisine_or_type: { label: "Cuisine / specialty", type: "text",  validate: () => null, placeholder: "Optional — e.g. Italian, Pho, Specialty Coffee" },
  website_url:    { label: "Website",              type: "url",    validate: () => null, placeholder: "https://" },

  // Plate
  seat_count:     { label: "Seats / capacity",     type: "number", validate: () => null },
  liquor_licence: { label: "Liquor licence #",     type: "text",   validate: () => null, placeholder: "Optional" },

  // Property
  property_listing_type: { label: "I am a", type: "select", validate: required(), options: PROPERTY_LISTING_TYPES },

  // Music
  artist_genre:   { label: "Genre / style",        type: "text",   validate: required() },

  // Agreements
  terms:          { label: "I agree to HustlePlate terms of service", type: "checkbox", validate: (v) => v === true ? null : "You must agree" },
  co_op_membership: { label: "I agree to join the Plaza Co-op", type: "checkbox", validate: (v) => v === true ? null : "Co-op membership is required to join Plaza" },
  marketing_optin: { label: "Send me launch updates and tips", type: "checkbox", validate: () => null },
};
